import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, Copy, Info, Phone } from "lucide-react";
import Seo from "../components/Seo";
import { paymentReturn, getOrderStatus } from "../lib/api";
import { clearPendingOrder } from "../lib/pendingOrders";
import { orderEmail } from "../lib/orderRef";
import { money, prettyDate, time12 } from "../lib/format";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];

const CodeBlock = ({ label, code, testId, big }) => {
  const [copied, setCopied] = useState(false);
  if (!code) return null;
  return (
    <div className="rounded-2xl border border-sand-border bg-sand p-4">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <div className="mt-2 flex items-center justify-between gap-3">
        <code data-testid={testId} className={`font-display font-bold text-navy ${big ? "text-2xl" : "text-base"}`}>
          {code}
        </code>
        <button
          type="button"
          data-testid={`${testId}-copy`}
          aria-label={`Copy ${label}`}
          onClick={() => {
            navigator.clipboard?.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }}
          className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-3 py-1.5 text-xs font-bold text-navy hover:bg-sand-card"
        >
          <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
};

export default function BookingConfirmation() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").trim().toUpperCase();
  // The platform's lookup needs the consumer's email as an INPUT. We never store
  // it, so it comes from this browser session -- and if that is empty (a fresh
  // browser, or the emailed link opened weeks later) we simply ask for it.
  const [email, setEmail] = useState(() => orderEmail(code));
  const [askEmail, setAskEmail] = useState("");
  const [status, setStatus] = useState({ loading: Boolean(code && orderEmail(code)), data: null, error: null });

  // Arriving here proves nothing about payment -- the platform's own summary decides.
  const load = useCallback(async () => {
    if (!code) return setStatus({ loading: false, data: null, error: "missing" });
    if (!email) return setStatus({ loading: false, data: null, error: null });
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const data = await paymentReturn(code, email);
      setStatus({ loading: false, data, error: null });
    } catch (err) {
      try {
        const fallback = await getOrderStatus(code, email);
        setStatus({ loading: false, data: { ...fallback, state: fallback.state === "paid" ? "paid" : "pending" }, error: null });
      } catch (e) {
        setStatus({ loading: false, data: null, error: e.payload?.userMessage || e.message });
      }
    }
  }, [code, email]);

  useEffect(() => {
    load();
  }, [load]);

  const d = status.data;
  // The platform resolves the payment against Stripe INLINE on this first read,
  // so its answer is final. There is nothing to poll for and no "confirming"
  // interval: `accepted` on the platform's own payment is the whole decision.
  const paid = d?.state === "paid";

  // Only the PLATFORM's confirmation retires the pending-payment reminder. Reaching
  // this page proves nothing on its own.
  useEffect(() => {
    if (paid && code) clearPendingOrder(code);
  }, [paid, code]);

  const summary = d?.summary || {};
  const outstanding = summary.outstanding ?? null;

  return (
    <>
      <Seo title="Your Reservation | West Branch Marina" path="/booking/confirmation" noindex />

      <div className="bg-navy pt-24 pb-12 lg:pt-32">
        <div className="mx-auto max-w-4xl px-5 lg:px-10">
          {paid ? (
            <CheckCircle2 className="h-10 w-10 text-lake" aria-hidden="true" />
          ) : (
            <Clock className="h-10 w-10 text-lake" aria-hidden="true" />
          )}
          <h1 data-testid="confirmation-heading" className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            {paid ? "You're booked" : "We're confirming your payment"}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/85">
            {paid ? (
              <>
                Your payment has gone through and your boat is reserved. We've emailed your
                confirmation — quote your code when you arrive.
              </>
            ) : (
              <>
                We haven't had confirmation from our payment provider yet. Keep this page — it's safe to
                reload — or call us on{" "}
                <a href={WB.phoneHref} className="font-bold text-ember hover:underline">{WB.phone}</a> and we'll
                check it for you.
              </>
            )}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-12 lg:px-10">
        {status.loading && (
          <p data-testid="confirmation-loading" className="text-sm text-slate-600">Checking your payment…</p>
        )}

        {!status.loading && !email && code && (
          <div data-testid="confirmation-email-prompt" className="rounded-2xl border border-sand-border bg-white p-8">
            <p className="font-display text-xl font-bold text-navy">Confirm it's you</p>
            <p className="mt-3 text-sm text-slate-600">
              Enter the email address you booked with and we'll fetch order{" "}
              <strong className="font-bold text-navy">{code}</strong> from our booking system.
            </p>
            <form
              className="mt-5 flex flex-col gap-3 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                const next = askEmail.trim();
                if (next) setEmail(next);
              }}
            >
              <input
                type="email"
                required
                data-testid="confirmation-email-input"
                value={askEmail}
                onChange={(e) => setAskEmail(e.target.value)}
                placeholder="you@example.com"
                aria-label="Email address you booked with"
                className="h-12 w-full rounded-xl border border-sand-border bg-white px-3 text-sm text-navy sm:max-w-xs"
              />
              <button
                type="submit"
                data-testid="confirmation-email-submit"
                className="h-12 rounded-full bg-navy px-6 text-sm font-bold text-white hover:bg-navy-surface"
              >
                Find my order
              </button>
            </form>
          </div>
        )}

        {!status.loading && email && (status.error || !d) && (
          <div data-testid="confirmation-missing" className="rounded-2xl border border-sand-border bg-white p-8">
            <p className="font-display text-xl font-bold text-navy">We couldn't load that order</p>
            <p className="mt-3 text-sm text-slate-600">
              {code ? (<>Your order confirmation code is <strong className="font-bold text-navy">{code}</strong>. </>) : null}
              Look your reservation up with your email address and confirmation code, or call us.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/booking/lookup" data-testid="confirmation-lookup-link" className="rounded-full bg-navy px-6 py-3 text-sm font-bold text-white hover:bg-navy-surface">
                Find my reservation
              </Link>
              <a href={WB.phoneHref} className="inline-flex items-center gap-2 rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy hover:bg-sand-card">
                <Phone className="h-4 w-4" aria-hidden="true" /> Call {WB.phone}
              </a>
            </div>
          </div>
        )}

        {!status.loading && d && (
          <>
            {!paid && (
              <div data-testid="confirmation-pending" role="status" className="mb-8 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
                <div className="text-sm text-amber-900">
                  <strong className="font-bold">Not confirmed yet.</strong> We won't say your payment
                  succeeded until our provider confirms it. If you've just paid, give it a moment and check
                  again.
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button type="button" data-testid="confirmation-recheck" onClick={load} className="rounded-full bg-navy px-5 py-2 text-xs font-bold text-white hover:bg-navy-surface">
                      Check again
                    </button>
                    <Link to={`/booking/payment?code=${code}`} data-testid="confirmation-pay-link" className="rounded-full border border-navy/20 px-5 py-2 text-xs font-bold text-navy hover:bg-white">
                      Pay now
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <CodeBlock label="Order confirmation code — use this for lookup & payment" code={d.confirmationCode} testId="order-confirmation-code" big />
              <div className="rounded-2xl border border-sand-border bg-sand p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {paid ? "Paid today" : "Total"}
                </p>
                <p data-testid="confirmation-order-total" className="mt-2 font-display text-2xl font-bold text-navy">
                  {money(paid ? summary.paidAmount : summary.totalDue)}
                </p>
                {outstanding > 0 ? (
                  <p data-testid="confirmation-balance-due" className="mt-1 text-sm text-slate-600">
                    Balance of <strong className="font-bold text-navy">{money(outstanding)}</strong> is due at
                    the marina when you check in — it is <strong className="font-bold">not</strong> paid yet.
                  </p>
                ) : (
                  paid && (
                    <p data-testid="confirmation-paid-in-full" className="mt-1 text-sm text-slate-600">
                      Paid in full — nothing further to pay.
                    </p>
                  )
                )}
                {summary.taxAmount ? (
                  <p data-testid="confirmation-tax" className="mt-1 text-xs text-slate-500">
                    Includes {money(summary.taxAmount)} sales tax
                  </p>
                ) : null}
              </div>
            </div>

            <h2 className="mt-10 font-display text-xl font-bold text-navy">Your reservation</h2>
            <ul data-testid="confirmation-lines" className="mt-4 space-y-3">
              {(d.lines || []).map((l, i) => (
                <li key={l.reservationId ?? i} data-testid={`confirmation-line-${i + 1}`} className="rounded-2xl border border-sand-border bg-white p-4">
                  <p className="font-display font-bold text-navy">{l.label || l.serviceName || `Boat ${i + 1}`}</p>
                  {(l.startDateTime || l.when) && (
                    <p className="mt-1 text-sm text-slate-600">
                      {l.startDateTime
                        ? `${prettyDate(l.startDateTime.slice(0, 10))} · ${time12(l.startDateTime.slice(11, 16))} – ${time12((l.endDateTime || "").slice(11, 16))}`
                        : l.when}
                      {l.quantity ? ` · qty ${l.quantity}` : ""}
                    </p>
                  )}
                  {l.reservationConfirmationCode && (
                    <>
                      <p className="mt-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                        Reservation code (this boat only)
                      </p>
                      <code data-testid={`reservation-confirmation-code-${i + 1}`} className="text-sm font-bold text-navy">
                        {l.reservationConfirmationCode}
                      </code>
                    </>
                  )}
                </li>
              ))}
            </ul>

            <p data-testid="confirmation-code-explainer" className="mt-6 flex items-start gap-2 rounded-xl bg-sand-card p-4 text-sm text-slate-700">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-lake" aria-hidden="true" />
              The <strong className="font-bold text-navy">order confirmation code</strong> covers your whole
              booking — quote that to us or use it to look your trip up. Each boat also has its own{" "}
              <strong className="font-bold text-navy">reservation code</strong>. To change or cancel, call us:
              cancellations can't be made online.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href={WB.phoneHref} data-testid="confirmation-call-link" className="rounded-full bg-ember px-6 py-3 text-sm font-bold text-white hover:bg-ember-hover">
                Call {WB.phone}
              </a>
              <Link to="/booking/lookup" className="rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy hover:bg-sand-card">
                Look up this reservation
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  );
}
